/* eslint-disable react-refresh/only-export-components */
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown, X, CaseSensitive, Regex, WholeWord } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  SearchInput,
  Segmented,
} from "@h2j-data-team/ui";
import { cn } from "@/lib/utils";
import type { CellHighlight } from "@/lib/highlight-matches";

export interface FindMatch {
  itemId: string;
  field: "name" | "content";
  start: number;
  length: number;
}

export interface FindReplaceItem {
  id: string;
  name: string;
  content: string;
}

export interface FindReplacement {
  itemId: string;
  field: "name" | "content";
  oldValue: string;
  newValue: string;
}

interface FindReplaceBarProps {
  items: FindReplaceItem[];
  onReplace: (replacement: FindReplacement) => void;
  onReplaceAll: (replacements: FindReplacement[]) => void;
  onHighlight: (itemId: string | null) => void;
  onClose: () => void;
  onMatchesChange?: (matches: FindMatch[], currentIndex: number) => void;
  showReplace?: boolean;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildPattern(searchTerm: string, useRegex: boolean, wholeWord: boolean): string {
  const base = useRegex ? searchTerm : escapeRegex(searchTerm);
  return wholeWord ? `\\b${base}\\b` : base;
}

/**
 * Given the full matches array and a specific item+field, compute the CellHighlight
 * segments for use with highlightMatches().
 */
export function getCellHighlights(
  matches: FindMatch[],
  currentIndex: number,
  itemId: string,
  field: "name" | "content",
): CellHighlight[] {
  return matches
    .map((m, i) => ({ ...m, globalIndex: i }))
    .filter((m) => m.itemId === itemId && m.field === field)
    .map((m) => ({ start: m.start, length: m.length, isCurrent: m.globalIndex === currentIndex }));
}

function ToggleButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={active ? "secondary" : "outline"}
      size="icon-compact"
      aria-pressed={active}
      aria-label={title}
      title={title}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function FindReplaceBar({
  items,
  onReplace,
  onReplaceAll,
  onHighlight,
  onClose,
  onMatchesChange,
  showReplace = true,
}: FindReplaceBarProps) {
  const { t } = useTranslation("common");
  const { t: ts } = useTranslation("single-app");
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [searchField, setSearchField] = useState<"content" | "name">("content");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  // The confirmation has no trigger of its own: focus goes back to the button that asked.
  const replaceAllRef = useRef<HTMLButtonElement>(null);
  const onHighlightRef = useRef(onHighlight);
  useEffect(() => {
    onHighlightRef.current = onHighlight;
  }, [onHighlight]);

  // Auto-focus on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const { matches, regexError } = useMemo(() => {
    if (!searchTerm) return { matches: [] as FindMatch[], regexError: false };

    let pattern: string;
    try {
      pattern = buildPattern(searchTerm, useRegex, wholeWord);
      new RegExp(pattern); // validate
    } catch {
      return { matches: [] as FindMatch[], regexError: true };
    }

    const flags = caseSensitive ? "g" : "gi";
    const result: FindMatch[] = [];

    for (const item of items) {
      const text = searchField === "name" ? item.name : item.content;
      const regex = new RegExp(pattern, flags);
      let m: RegExpExecArray | null;
      while ((m = regex.exec(text)) !== null) {
        result.push({ itemId: item.id, field: searchField, start: m.index, length: m[0].length });
        if (m[0].length === 0) regex.lastIndex++;
      }
    }

    return { matches: result, regexError: false };
  }, [searchTerm, items, searchField, caseSensitive, useRegex, wholeWord]);

  // Reset match index when matches change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentMatchIndex(0);
    onHighlightRef.current(matches.length > 0 ? matches[0].itemId : null);
  }, [matches]);

  // Notify parent of match state
  useEffect(() => {
    onMatchesChange?.(matches, currentMatchIndex);
  }, [matches, currentMatchIndex, onMatchesChange]);

  const navigateTo = useCallback(
    (index: number) => {
      if (!matches.length) return;
      const next = ((index % matches.length) + matches.length) % matches.length;
      setCurrentMatchIndex(next);
      onHighlightRef.current(matches[next].itemId);
    },
    [matches],
  );

  const handleNext = useCallback(
    () => navigateTo(currentMatchIndex + 1),
    [navigateTo, currentMatchIndex],
  );
  const handlePrev = useCallback(
    () => navigateTo(currentMatchIndex - 1),
    [navigateTo, currentMatchIndex],
  );

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) handlePrev();
      else handleNext();
    }
  };

  const handleReplaceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleReplace();
    }
  };

  // Escape closes from anywhere in the bar, not only from the two inputs.
  const handleBarKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") onClose();
  };

  const handleReplace = useCallback(() => {
    if (!matches.length) return;
    const match = matches[currentMatchIndex];
    const item = items.find((i) => i.id === match.itemId);
    if (!item) return;

    const text = match.field === "name" ? item.name : item.content;
    let newValue: string;

    if (useRegex) {
      try {
        const pattern = buildPattern(searchTerm, useRegex, wholeWord);
        const matchText = text.slice(match.start, match.start + match.length);
        newValue =
          text.slice(0, match.start) +
          matchText.replace(new RegExp(pattern, caseSensitive ? "" : "i"), replaceTerm) +
          text.slice(match.start + match.length);
      } catch {
        return;
      }
    } else {
      newValue = text.slice(0, match.start) + replaceTerm + text.slice(match.start + match.length);
    }

    onReplace({ itemId: match.itemId, field: match.field, oldValue: text, newValue });
    navigateTo(currentMatchIndex);
  }, [
    matches,
    currentMatchIndex,
    items,
    useRegex,
    searchTerm,
    wholeWord,
    caseSensitive,
    replaceTerm,
    onReplace,
    navigateTo,
  ]);

  const computeReplaceAll = useCallback((): FindReplacement[] => {
    const grouped = new Map<string, FindMatch[]>();
    for (const m of matches) {
      const key = `${m.itemId}:${m.field}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(m);
    }

    const replacements: FindReplacement[] = [];

    for (const [key, groupMatches] of grouped) {
      const colonIdx = key.indexOf(":");
      const itemId = key.slice(0, colonIdx);
      const field = key.slice(colonIdx + 1) as "name" | "content";
      const item = items.find((i) => i.id === itemId);
      if (!item) continue;
      const text = field === "name" ? item.name : item.content;

      let newValue: string;
      if (useRegex) {
        try {
          const pattern = buildPattern(searchTerm, useRegex, wholeWord);
          const flags = caseSensitive ? "g" : "gi";
          newValue = text.replace(new RegExp(pattern, flags), replaceTerm);
        } catch {
          continue;
        }
      } else {
        newValue = text;
        for (const m of [...groupMatches].reverse()) {
          newValue = newValue.slice(0, m.start) + replaceTerm + newValue.slice(m.start + m.length);
        }
      }

      replacements.push({ itemId, field, oldValue: text, newValue });
    }

    return replacements;
  }, [matches, items, useRegex, searchTerm, wholeWord, caseSensitive, replaceTerm]);

  const uniqueItemCount = useMemo(() => new Set(matches.map((m) => m.itemId)).size, [matches]);

  const matchCount = matches.length;
  const occurrencesLabel = t("findReplace.replaceAllOccurrences", { count: matchCount });
  const itemsLabel = t("findReplace.replaceAllItems", { count: uniqueItemCount });
  const matchLabel =
    matchCount === 0
      ? searchTerm
        ? t("findReplace.noResults")
        : ""
      : t("findReplace.currentOfTotal", { current: currentMatchIndex + 1, total: matchCount });

  return (
    <>
      <div
        role="group"
        aria-label={ts("findReplaceBar.label")}
        onKeyDown={handleBarKeyDown}
        className="w-[360px] rounded-card border border-hairline bg-background p-3 shadow-elevated"
      >
        {/* Find row: input + count + nav + close */}
        <div className="flex items-center gap-1">
          <SearchInput
            ref={searchInputRef}
            size="compact"
            containerClassName="min-w-0 flex-1"
            className="font-mono"
            value={searchTerm}
            onValueChange={setSearchTerm}
            clearLabel={ts("findReplaceBar.clear")}
            onKeyDown={handleSearchKeyDown}
            placeholder={t("findReplace.findPlaceholder")}
            aria-label={t("findReplace.findPlaceholder")}
            error={regexError}
          />
          <span
            role="status"
            className={cn(
              "min-w-[40px] shrink-0 text-center text-[11px] tabular-nums",
              matchCount === 0 && searchTerm ? "text-status-danger-foreground" : "text-dim",
            )}
          >
            {matchLabel}
          </span>
          <Button
            variant="ghost"
            size="icon-compact"
            className="shrink-0"
            onClick={handlePrev}
            disabled={matchCount === 0}
            aria-label={t("findReplace.previousMatchTitle")}
            title={t("findReplace.previousMatchTitle")}
          >
            <ChevronUp />
          </Button>
          <Button
            variant="ghost"
            size="icon-compact"
            className="shrink-0"
            onClick={handleNext}
            disabled={matchCount === 0}
            aria-label={t("findReplace.nextMatchTitle")}
            title={t("findReplace.nextMatchTitle")}
          >
            <ChevronDown />
          </Button>
          <Button
            variant="ghost"
            size="icon-compact"
            className="shrink-0"
            onClick={onClose}
            aria-label={t("findReplace.closeTitle")}
            title={t("findReplace.closeTitle")}
          >
            <X />
          </Button>
        </div>

        {/* Options row: match toggles + search scope */}
        <div className="mt-2 flex items-center gap-2">
          <div className="flex items-center gap-1">
            <ToggleButton
              active={caseSensitive}
              onClick={() => setCaseSensitive((v) => !v)}
              title={t("findReplace.caseSensitiveTitle")}
            >
              <CaseSensitive />
            </ToggleButton>
            <ToggleButton
              active={wholeWord}
              onClick={() => setWholeWord((v) => !v)}
              title={t("findReplace.wholeWordTitle")}
            >
              <WholeWord />
            </ToggleButton>
            <ToggleButton
              active={useRegex}
              onClick={() => setUseRegex((v) => !v)}
              title={t("findReplace.useRegexTitle")}
            >
              <Regex />
            </ToggleButton>
          </div>
          <div className="ml-auto">
            <Segmented<"content" | "name">
              size="compact"
              label={ts("findReplaceBar.searchIn")}
              value={searchField}
              onValueChange={setSearchField}
              options={[
                { value: "content", label: t("findReplace.content") },
                { value: "name", label: t("findReplace.name") },
              ]}
            />
          </div>
        </div>

        {/* Replace input + actions (optional) */}
        {showReplace && (
          <>
            <Input
              size="compact"
              value={replaceTerm}
              onChange={(e) => setReplaceTerm(e.target.value)}
              onKeyDown={handleReplaceKeyDown}
              placeholder={t("findReplace.replacePlaceholder")}
              aria-label={t("findReplace.replacePlaceholder")}
              className="mt-2 font-mono"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={handleReplace}
                disabled={matchCount === 0}
              >
                {t("findReplace.replace")}
              </Button>
              <Button
                ref={replaceAllRef}
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setShowConfirm(true)}
                disabled={matchCount === 0}
              >
                {t("findReplace.replaceAll")}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Replace All confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm} restoreFocusRef={replaceAllRef}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("findReplace.replaceAllTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("findReplace.replaceAllDescription", {
                occurrences: occurrencesLabel,
                items: itemsLabel,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">{t("cancel")}</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                onClick={() => {
                  onReplaceAll(computeReplaceAll());
                  setShowConfirm(false);
                }}
              >
                {t("findReplace.replaceAll")}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
